import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useContent } from "../lib/content";
import { Phone, ChevronRight, Mail } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useContent();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setOkMsg("");
    if (!email.trim()) {
      setErr("Kérem adja meg az e-mail címét.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: email.trim() });
      setOkMsg(
        data?.message ||
          "Ha a megadott e-mail cím létezik, küldtünk egy visszaállítási linket."
      );
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Top utility bar */}
      <div className="topbar">
        <div className="max-w-[1200px] mx-auto px-4 h-9 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Phone size={11} />
            <span>{t("topbar_phone_label", "HÍVJON")}: {t("topbar_phone", "+36 20 9 40 40 04")}</span>
          </div>
          <nav className="flex items-center gap-1 text-[11px] uppercase">
            <Link to="/" className="px-2 py-1 hover:text-[color:var(--brand)]">Kezdőlap</Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <Link to="/login" className="px-2 py-1 hover:text-[color:var(--brand)]">Belépés</Link>
            <ChevronRight size={10} style={{ color: "var(--text-disabled)" }} />
            <span className="px-2 py-1 font-bold" style={{ color: "var(--brand)" }}>Elfelejtett jelszó</span>
          </nav>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-3">
            <div
              className="w-14 h-14 flex items-center justify-center rounded shrink-0"
              style={{ background: "var(--text)", color: "#fff", fontWeight: 900, fontSize: 20, letterSpacing: "-0.02em" }}
            >
              {t("header_logo_initials", "P24")}
            </div>
            <div className="leading-tight">
              <div className="font-bold text-xl" style={{ color: "var(--text)" }}>{t("header_company_name", "prnds24.hu")}</div>
              <div className="text-xs" style={{ color: "var(--text-mute)" }}>{t("header_company_subtitle", "OEM Alkatrészek · Nagyker")}</div>
            </div>
          </Link>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="max-w-[1200px] mx-auto px-4">
          <Link to="/">Kezdőlap</Link> / <Link to="/login">Bejelentkezés</Link> / <span style={{ color: "var(--text)" }}>Elfelejtett jelszó</span>
        </div>
      </div>

      <main className="flex-1 bg-[color:var(--surface-elev,#f5f5f5)]">
        <div className="max-w-[700px] w-full mx-auto px-4 py-10">
          <h1 className="text-2xl font-bold mb-3 uppercase tracking-wide" style={{ color: "var(--text)" }}>
            Elfelejtett jelszó
          </h1>
          <div className="border-b-2 mb-6" style={{ borderColor: "var(--brand)" }} />

          <div className="card-elev">
            <div className="section-title">Jelszó visszaállítás kérése</div>
            <div className="p-6">
              <p className="text-sm mb-5" style={{ color: "var(--text-mute)" }}>
                Adja meg a regisztrációkor használt e-mail címét. Ha a fiók létezik,
                küldünk Önnek egy linket, amellyel beállíthat egy új jelszót. A link
                <strong> 1 óráig érvényes</strong>.
              </p>

              {okMsg ? (
                <div
                  className="text-sm py-3 px-4 mb-4 flex items-start gap-3"
                  style={{ background: "#EFFAE9", color: "#2C6E1F", border: "1px solid #C9E5B7", borderRadius: 4 }}
                  data-testid="forgot-success"
                >
                  <Mail size={16} className="mt-0.5 shrink-0" />
                  <div>
                    <strong>Sikeres kérés.</strong>
                    <div className="mt-1">{okMsg}</div>
                    <div className="mt-2 text-xs" style={{ color: "var(--text-mute)" }}>
                      Ha pár percen belül nem érkezik meg az e-mail, ellenőrizze a SPAM/Levélszemét mappát is.
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 sm:items-center">
                    <label className="text-sm font-bold">
                      E-Mail cím: <span style={{ color: "var(--out-stock)" }}>*</span>
                    </label>
                    <input
                      type="email"
                      className="input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoFocus
                      data-testid="forgot-email-input"
                    />
                  </div>

                  {err && (
                    <div
                      className="text-sm py-2 px-3"
                      style={{ background: "#FEE", color: "var(--out-stock)", border: "1px solid #FCC", borderRadius: 4 }}
                      data-testid="forgot-error"
                    >
                      <strong>HIBA:</strong> {err}
                    </div>
                  )}

                  <div className="grid sm:grid-cols-[180px_1fr] gap-3 items-center pt-2">
                    <span />
                    <div className="flex items-center justify-between gap-3">
                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary"
                        data-testid="forgot-submit-button"
                      >
                        {loading ? "Egy pillanat..." : "Visszaállítási link küldése"}
                      </button>
                      <Link
                        to="/login"
                        className="text-xs hover:underline"
                        style={{ color: "var(--brand)" }}
                        data-testid="forgot-back-link"
                      >
                        ← Vissza a bejelentkezéshez
                      </Link>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="mt-10 border-t" style={{ background: "var(--surface-elev)", borderColor: "var(--border)" }}>
        <div className="max-w-[1200px] mx-auto px-4 py-5 text-xs flex flex-wrap items-center justify-between gap-3" style={{ color: "var(--text-mute)" }}>
          <div>{t("footer_text", "prnds24.hu — OEM Alkatrész Nagykereskedés")} · © {new Date().getFullYear()}</div>
          <div>{t("footer_links", "Bejelentkezés · Bevásárlókosár · Kapcsolat")}</div>
        </div>
      </footer>
    </div>
  );
}
